/**
 * Decides when a fragment model's loading badge is visible, from the model's
 * tile add/remove events (`model.tiles.onItemSet` / `onItemDeleted`).
 *
 * `FragmentsModel.isBusy` can't drive a badge: it reads true ~80 % of the
 * time on an idle model, because every `update()` marks the view as
 * processing. Tile events, by contrast, arrive in bursts while geometry is
 * streaming in (initial load, zoom, pan) and stop entirely once the view is
 * complete — measured on an 8 M-triangle terrain model.
 *
 * - `initial`: from load until tiles have been quiet for `settleMs`. Initial
 *   streaming has a lull of 1–2.5 s between the first few tiles and the main
 *   burst (measured), so the window has to be wider than that or the badge
 *   hides with a tenth of the model drawn.
 * - `refine`: a later burst, shown only once it has lasted `refineMinMs` (so
 *   routine refreshes don't flicker a badge) and hidden after `refineQuietMs`
 *   without events — long enough to bridge the ~1 s gaps inside one burst.
 */
export type StreamStage = 'initial' | 'refine';

export interface StreamTracker {
  tileEvent(now: number): void;
  /** The stage to show at `now`, or null to hide the badge. */
  poll(now: number): StreamStage | null;
}

export function createStreamTracker({
  now,
  settleMs = 3000,
  refineMinMs = 500,
  refineQuietMs = 1200,
  giveUpMs = 10_000,
}: {
  now: number;
  settleMs?: number;
  refineMinMs?: number;
  refineQuietMs?: number;
  /** Settle anyway if no tile ever arrives (empty or fully culled model). */
  giveUpMs?: number;
}): StreamTracker {
  const startedAt = now;
  let lastEvent: number | null = null;
  let settled = false;
  let burstStart: number | null = null;

  return {
    tileEvent(t) {
      lastEvent = t;
      if (settled && burstStart === null) burstStart = t;
    },
    poll(t) {
      if (!settled) {
        const quiet = lastEvent !== null ? t - lastEvent >= settleMs : t - startedAt >= giveUpMs;
        if (!quiet) return 'initial';
        settled = true;
        burstStart = null;
        return null;
      }
      if (burstStart === null) return null;
      if (lastEvent !== null && t - lastEvent >= refineQuietMs) {
        burstStart = null;
        return null;
      }
      return t - burstStart >= refineMinMs ? 'refine' : null;
    },
  };
}
